import { Input, Select, SelectItem } from '@heroui/react'
import { Trash2 } from 'lucide-react'
import { Button as HeroButton } from '@heroui/react'
import type { RuleTrigger, TriggerType, TriggerOperator } from '../../types'
import { TRIGGER_TYPE_LABELS, TRIGGER_OPERATOR_LABELS, TRIGGER_OPERATORS } from '../../types'
import { ProductPicker } from '../ProductPicker'

interface TriggerEditorProps {
  trigger: RuleTrigger
  onChange: (updates: Partial<RuleTrigger>) => void
  onDelete: () => void
}

export function TriggerEditor({ trigger, onChange, onDelete }: TriggerEditorProps) {
  const availableOperators = TRIGGER_OPERATORS[trigger.type] || ['equals']
  const isProductTrigger = trigger.type === 'product_in_cart'
  const isMultiValueOperator = trigger.operator === 'in' || trigger.operator === 'not_in'
  const productValue: string | string[] = Array.isArray(trigger.value)
    ? trigger.value
    : isMultiValueOperator
      ? (trigger.value ? [String(trigger.value)] : [])
      : String(trigger.value)

  return (
    <div
      className="flex items-start gap-2 p-2 rounded-lg border border-fn-border bg-fn-muted"
    >
      <div className="flex-1 space-y-2">
        <div className="grid grid-cols-3 gap-2">
        <Select
          size="sm"
          variant="bordered"
          selectedKeys={[trigger.type]}
          onSelectionChange={(keys) => {
            const type = Array.from(keys)[0] as TriggerType
            if (type) onChange({ type, operator: TRIGGER_OPERATORS[type]?.[0] || 'equals', value: '' })
          }}
          aria-label="Trigger type"
        >
          {Object.entries(TRIGGER_TYPE_LABELS).map(([key, label]) => (
            <SelectItem key={key}>{label}</SelectItem>
          ))}
        </Select>

        <Select
          size="sm"
          variant="bordered"
          selectedKeys={[trigger.operator]}
          onSelectionChange={(keys) => {
            const op = Array.from(keys)[0] as TriggerOperator
            if (op) onChange({ operator: op })
          }}
          aria-label="Operator"
        >
          {availableOperators.map((op) => (
            <SelectItem key={op}>{TRIGGER_OPERATOR_LABELS[op]}</SelectItem>
          ))}
        </Select>

        {!isProductTrigger && (
          <Input
            size="sm"
            variant="bordered"
            placeholder="Value"
            value={String(trigger.value)}
            onValueChange={(val) => onChange({ value: val })}
            aria-label="Value"
          />
        )}
        </div>

        {isProductTrigger && (
          <ProductPicker
            label={isMultiValueOperator ? 'Products' : 'Product'}
            mode={isMultiValueOperator ? 'multiple' : 'single'}
            value={productValue}
            onChange={(nextValue) => onChange({ value: nextValue })}
          />
        )}
      </div>

      <HeroButton
        isIconOnly
        size="sm"
        variant="light"
        color="danger"
        onPress={onDelete}
        aria-label="Delete trigger"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </HeroButton>
    </div>
  )
}
